import { useState, useEffect } from 'react';
import { Download, Upload, Clock, CheckCircle, AlertCircle, HardDrive, Trash2 } from 'lucide-react';
import { backupService } from '../services/backup.service';

export default function BackupSettings() {
  const [lastBackup, setLastBackup] = useState(null);
  const [backupHistory, setBackupHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedEntities, setSelectedEntities] = useState(['all']);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadBackupData();
  }, []);

  const loadBackupData = async () => {
    try {
      const [last, history, backupStats] = await Promise.all([
        backupService.getLastBackup(),
        backupService.getBackupHistory(),
        backupService.getBackupStats()
      ]);
      
      setLastBackup(last);
      setBackupHistory(history);
      setStats(backupStats);
    } catch (err) {
      console.error('Failed to load backup data:', err);
      setError('Failed to load backup information');
    }
  };

  const handleDownloadBackup = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await backupService.downloadBackup({
        entities: selectedEntities
      });
      
      if (!result.canceled) {
        await loadBackupData();
      }
    } catch (err) {
      console.error('Backup failed:', err);
      setError(err.message || 'Backup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCleanup = async () => {
    if (!confirm('Delete backups older than 30 days?')) return;
    
    try {
      await backupService.cleanupOldBackups(30);
      await loadBackupData();
    } catch (err) {
      console.error('Cleanup failed:', err);
      setError('Cleanup failed');
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100/50">
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-slate-900 mb-2">
            Backup & Recovery
          </h1>
          <p className="text-sm text-slate-600">
            Protect your financial data with automatic and manual backups
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-rose-50/80 border border-rose-200/50 rounded-xl flex items-start gap-3">
            <AlertCircle size={18} className="text-rose-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-rose-900">{error}</p>
            </div>
          </div>
        )}

        {/* Backup Status Card */}
        <div className="mb-6 p-6 bg-white rounded-2xl shadow-sm border border-slate-200/60">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-base font-semibold text-slate-900 mb-1">
                Backup Status
              </h2>
              <p className="text-sm text-slate-600">
                Your data is automatically backed up
              </p>
            </div>
            {lastBackup && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50/60 rounded-lg">
                <CheckCircle size={14} className="text-emerald-600" />
                <span className="text-xs font-medium text-emerald-900">Active</span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-slate-50/60 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <Clock size={16} className="text-slate-500" />
                <span className="text-xs font-medium text-slate-600">Last Backup</span>
              </div>
              <p className="text-lg font-semibold text-slate-900">
                {formatDate(lastBackup?.created_at)}
              </p>
            </div>

            <div className="p-4 bg-slate-50/60 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <HardDrive size={16} className="text-slate-500" />
                <span className="text-xs font-medium text-slate-600">Total Backups</span>
              </div>
              <p className="text-lg font-semibold text-slate-900">
                {stats?.totalBackups || 0}
              </p>
            </div>

            <div className="p-4 bg-slate-50/60 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <HardDrive size={16} className="text-slate-500" />
                <span className="text-xs font-medium text-slate-600">Storage Used</span>
              </div>
              <p className="text-lg font-semibold text-slate-900">
                {formatBytes(stats?.totalSize)}
              </p>
            </div>
          </div>
        </div>

        {/* Manual Backup Card */}
        <div className="mb-6 p-6 bg-white rounded-2xl shadow-sm border border-slate-200/60">
          <h2 className="text-base font-semibold text-slate-900 mb-4">
            Create Manual Backup
          </h2>

          <div className="space-y-3 mb-6">
            <label className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50/60 cursor-pointer transition-colors">
              <input
                type="radio"
                name="backup-type"
                checked={selectedEntities.includes('all')}
                onChange={() => setSelectedEntities(['all'])}
                className="w-4 h-4 text-blue-600"
              />
              <div>
                <p className="text-sm font-medium text-slate-900">Full Backup</p>
                <p className="text-xs text-slate-600">All invoices, quotations, clients, and settings</p>
              </div>
            </label>

            <label className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50/60 cursor-pointer transition-colors">
              <input
                type="radio"
                name="backup-type"
                checked={selectedEntities.includes('invoices') && !selectedEntities.includes('all')}
                onChange={() => setSelectedEntities(['invoices'])}
                className="w-4 h-4 text-blue-600"
              />
              <div>
                <p className="text-sm font-medium text-slate-900">Invoices Only</p>
                <p className="text-xs text-slate-600">Export all invoices and invoice items</p>
              </div>
            </label>

            <label className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50/60 cursor-pointer transition-colors">
              <input
                type="radio"
                name="backup-type"
                checked={selectedEntities.includes('clients') && !selectedEntities.includes('all')}
                onChange={() => setSelectedEntities(['clients'])}
                className="w-4 h-4 text-blue-600"
              />
              <div>
                <p className="text-sm font-medium text-slate-900">Clients Only</p>
                <p className="text-xs text-slate-600">Export all client information</p>
              </div>
            </label>
          </div>

          <button
            onClick={handleDownloadBackup}
            disabled={loading}
            className="w-full px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Creating Backup...
              </>
            ) : (
              <>
                <Download size={16} />
                Download Backup
              </>
            )}
          </button>
        </div>

        {/* Backup History */}
        <div className="p-6 bg-white rounded-2xl shadow-sm border border-slate-200/60">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-slate-900">
              Backup History
            </h2>
            {backupHistory.length > 0 && (
              <button
                onClick={handleCleanup}
                className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1.5 transition-colors"
              >
                <Trash2 size={14} />
                Clean Up Old Backups
              </button>
            )}
          </div>

          {backupHistory.length === 0 ? (
            <div className="py-12 text-center">
              <HardDrive size={32} className="mx-auto mb-3 text-slate-300" />
              <p className="text-sm text-slate-600 mb-1">No backups yet</p>
              <p className="text-xs text-slate-500">Create your first backup above</p>
            </div>
          ) : (
            <div className="space-y-2">
              {backupHistory.slice(0, 10).map((backup) => (
                <div
                  key={backup.id}
                  className="p-4 rounded-xl border border-slate-200/60 hover:bg-slate-50/40 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-slate-900">
                          {backup.backup_type === 'manual' ? 'Manual Backup' : 'Auto Backup'}
                        </p>
                        {backup.status === 'completed' ? (
                          <CheckCircle size={14} className="text-emerald-600" />
                        ) : (
                          <AlertCircle size={14} className="text-rose-600" />
                        )}
                      </div>
                      <p className="text-xs text-slate-600 mb-2">
                        {new Date(backup.created_at).toLocaleString()}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span>{formatBytes(backup.file_size)}</span>
                        {backup.entitiesCount && (
                          <>
                            <span>•</span>
                            <span>
                              {backup.entitiesCount.invoices || 0} invoices,{' '}
                              {backup.entitiesCount.clients || 0} clients
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="mt-6 p-4 bg-blue-50/60 border border-blue-200/50 rounded-xl">
          <div className="flex items-start gap-3">
            <CheckCircle size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-blue-900 mb-1">
                Your data is safe
              </p>
              <p className="text-xs text-blue-700">
                Backups are stored locally on your computer. You can download them anytime and restore if needed.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
